import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { Building2, ChevronLeft, Eye, EyeOff, ShieldCheck, Sparkles } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import Checkbox from "../../../../shared/ui/form/input/Checkbox";
import { isPasswordValid, PasswordRequirements } from "../../../auth";
import { useTherapist } from "../../../../context/useTherapist";
import { CenterType } from "../../../center";
import {
    enableOwnerMfa,
    hasSignedInUser,
    registerCenter,
    reloadEmailVerified,
    signInOwnerAndSendVerification,
} from "../../application/centerSignUp";

type Step = "center" | "owner" | "verify" | "mfa";

/**
 * Signing up a centre.
 *
 * A separate form from therapist sign-up, not a mode of it. The organisation
 * is the first thing that exists here, the person registering is its
 * administrator, and whether they also treat people is asked rather than
 * assumed — both answers are ordinary, and guessing puts an account in the
 * wrong place.
 *
 * Only the centre's name and vertical are required. Tax id, address and
 * contact details are collected later from the settings screen: paperwork
 * should not stand between a practice and its first login.
 */
/**
 * Where a new centre lands.
 *
 * Not the centre itself: it has bought nothing yet, so its seats carry no
 * plan and there is nothing in there to use. The first thing to do is choose
 * how many seats and which modules, which is what this screen is.
 */
const AFTER_SIGN_UP = "/center/subscription";

export default function CenterSignUpForm({ invitationToken }: { invitationToken?: string }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setTherapist, refreshTherapist } = useTherapist();

    const [step, setStep] = useState<Step>("center");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const [name, setName] = useState("");
    const [type, setType] = useState<CenterType>("therapy");
    const [ownerFirstName, setOwnerFirstName] = useState("");
    const [ownerLastName, setOwnerLastName] = useState("");
    const [ownerEmail, setOwnerEmail] = useState("");
    const [ownerPassword, setOwnerPassword] = useState("");
    const [ownerPractises, setOwnerPractises] = useState(true);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const [qrCode, setQrCode] = useState<string | null>(null);
    const [totpCode, setTotpCode] = useState("");

    const centerStepIsComplete = name.trim().length > 0;
    const ownerStepIsComplete =
        ownerFirstName.trim() &&
        ownerLastName.trim() &&
        ownerEmail.trim() &&
        isPasswordValid(ownerPassword) &&
        acceptedTerms;

    const handleRegister = async () => {
        setErrorMessage(null);
        setLoading(true);
        try {
            const registration = await registerCenter({
                name: name.trim(),
                type,
                owner_first_name: ownerFirstName.trim(),
                owner_last_name: ownerLastName.trim(),
                owner_email: ownerEmail.trim(),
                owner_password: ownerPassword,
                owner_practises: ownerPractises,
                country: "ES",
                language: "es",
                invitation_token: invitationToken,
            });
            setQrCode(registration.mfa.qr_code_base64);

            await signInOwnerAndSendVerification(ownerEmail.trim(), ownerPassword);

            setStep("verify");
        } catch (err: unknown) {
            const error = err as { response?: { status?: number; data?: { detail?: string } }; code?: string };
            if (error?.response?.data?.detail === "center_invitation_invalid") {
                // The code was switched off between opening the page and
                // submitting. Signing them up as a paying centre instead
                // would be charging somebody who believes they were given
                // something.
                setErrorMessage(t("center.signUp.inviteInvalidTitle"));
            } else if (error?.response?.status === 409 || error?.code === "auth/email-already-in-use") {
                setErrorMessage(t("auth.errorExists"));
            } else if (error?.response?.status === 422 || error?.code === "auth/invalid-email") {
                setErrorMessage(t("auth.errorData"));
            } else {
                setErrorMessage(t("auth.errorReg"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmEmailVerified = async () => {
        if (!hasSignedInUser()) return;
        setLoading(true);
        setErrorMessage(null);
        try {
            if (await reloadEmailVerified()) {
                setStep("mfa");
            } else {
                setErrorMessage(t("auth.emailVerification.notVerifiedYet"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEnableMfa = async () => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const therapist = await enableOwnerMfa(totpCode);
            setTherapist(therapist);
            navigate(AFTER_SIGN_UP);
        } catch {
            setErrorMessage(t("auth.errorInvalidMfa"));
        } finally {
            setLoading(false);
        }
    };

    // Same choice a therapist gets: MFA can be switched on later from the
    // profile. The session is already open, so the owner goes straight into
    // their centre rather than back to the login screen.
    const handleSkipMfa = async () => {
        await refreshTherapist();
        navigate(AFTER_SIGN_UP);
    };

    return (
        <div className="flex flex-col justify-center w-full max-w-md mx-auto">
            <div className="mb-3 inline-flex items-center gap-1.5 self-start rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {t("invite.plusBadge")}
            </div>
            <h1 className="mb-1 flex items-center gap-2 text-2xl font-semibold text-content-primary">
                <Building2 size={22} aria-hidden="true" />
                {t("center.signUp.title")}
            </h1>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                {t("center.signUp.subtitle")}
            </p>

            {step === "center" && (
                <form
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setStep("owner");
                    }}
                >
                    <div>
                        <Label htmlFor="signup-center-name">{t("center.fields.name")}</Label>
                        <Input
                            id="signup-center-name"
                            name="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("center.signUp.namePlaceholder")}
                            required
                            autoFocus
                        />
                    </div>

                    <fieldset>
                        <legend className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                            {t("center.fields.type")}
                        </legend>
                        <div className="grid gap-2 sm:grid-cols-2">
                            {(["therapy", "training"] as CenterType[]).map((option) => (
                                <label
                                    key={option}
                                    className={`flex cursor-pointer flex-col gap-1 rounded-xl border p-3 text-sm ${
                                        type === option
                                            ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                            : "border-gray-200 dark:border-gray-800"
                                    }`}
                                >
                                    <span className="flex items-center gap-2 font-medium text-content-primary">
                                        <input
                                            type="radio"
                                            name="center-type"
                                            value={option}
                                            checked={type === option}
                                            onChange={() => setType(option)}
                                        />
                                        {t(`center.types.${option}`)}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {t(`center.types.${option}Hint`)}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <Button type="submit" disabled={!centerStepIsComplete} fullWidth>
                        {t("common.continue")}
                    </Button>

                    <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                        {t("center.signUp.soloPrompt")}{" "}
                        <Link to="/signup" className="text-brand-600 hover:underline">
                            {t("center.signUp.soloLink")}
                        </Link>
                    </p>
                </form>
            )}

            {step === "owner" && (
                <form
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleRegister();
                    }}
                >
                    <button
                        type="button"
                        onClick={() => setStep("center")}
                        className="flex items-center gap-1 self-start text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
                    >
                        <ChevronLeft size={16} aria-hidden="true" />
                        {t("common.back")}
                    </button>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <Label htmlFor="signup-owner-first-name">
                                {t("center.signUp.ownerFirstName")}
                            </Label>
                            <Input
                                id="signup-owner-first-name"
                                name="owner_first_name"
                                value={ownerFirstName}
                                onChange={(e) => setOwnerFirstName(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="signup-owner-last-name">
                                {t("center.signUp.ownerLastName")}
                            </Label>
                            <Input
                                id="signup-owner-last-name"
                                name="owner_last_name"
                                value={ownerLastName}
                                onChange={(e) => setOwnerLastName(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="signup-owner-email">{t("center.signUp.ownerEmail")}</Label>
                        <Input
                            id="signup-owner-email"
                            name="owner_email"
                            type="email"
                            value={ownerEmail}
                            onChange={(e) => setOwnerEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <Label htmlFor="signup-owner-password">
                            {t("center.signUp.ownerPassword")}
                        </Label>
                        <div className="relative">
                            <Input
                                id="signup-owner-password"
                                name="owner_password"
                                type={showPassword ? "text" : "password"}
                                value={ownerPassword}
                                onChange={(e) => setOwnerPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((shown) => !shown)}
                                aria-label={
                                    showPassword ? t("center.signUp.hidePassword") : t("center.signUp.showPassword")
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-tertiary"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <PasswordRequirements password={ownerPassword} />
                    </div>

                    {/* Asked, never inferred: the owner of a small practice
                        usually treats people in it, the owner of a large one
                        often does not, and the answer decides whether this
                        account is ever offered as somebody's therapist. */}
                    <Checkbox
                        checked={ownerPractises}
                        onChange={setOwnerPractises}
                        label={
                            type === "training"
                                ? t("center.signUp.ownerTrains")
                                : t("center.signUp.ownerPractises")
                        }
                    />

                    <div className="flex items-start gap-3">
                        <Checkbox
                            id="center-terms"
                            checked={acceptedTerms}
                            onChange={setAcceptedTerms}
                            className="mt-1"
                        />
                        <label
                            htmlFor="center-terms"
                            className="cursor-pointer select-none text-sm leading-relaxed text-content-tertiary"
                        >
                            <Trans
                                i18nKey="auth.terms"
                                components={{
                                    1: (
                                        <Link
                                            to="/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-bold text-brand-500 hover:underline"
                                        />
                                    ),
                                    3: (
                                        <Link
                                            to="/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-bold text-brand-500 hover:underline"
                                        />
                                    ),
                                }}
                            />
                        </label>
                    </div>

                    {errorMessage && (
                        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                            {errorMessage}
                        </p>
                    )}

                    <Button
                        type="submit"
                        disabled={!ownerStepIsComplete || loading}
                        loading={loading}
                        fullWidth
                    >
                        {t("center.signUp.submit")}
                    </Button>
                </form>
            )}

            {step === "verify" && (
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t("center.signUp.emailSentTo", { email: ownerEmail })}
                    </p>
                    {errorMessage && (
                        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                            {errorMessage}
                        </p>
                    )}
                    <Button onClick={handleConfirmEmailVerified} loading={loading} fullWidth>
                        {t("auth.emailVerification.confirm")}
                    </Button>
                </div>
            )}

            {step === "mfa" && (
                <div className="flex flex-col gap-4">
                    <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <ShieldCheck size={18} aria-hidden="true" />
                        {t("center.signUp.mfaHint")}
                    </p>
                    {qrCode && (
                        <img
                            src={`data:image/png;base64,${qrCode}`}
                            alt={t("center.signUp.mfaQrAlt")}
                            className="mx-auto h-44 w-44"
                        />
                    )}
                    <div>
                        <Label htmlFor="signup-totp">{t("center.signUp.mfaCode")}</Label>
                        <Input
                            id="signup-totp"
                            name="totp_code"
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value)}
                        />
                    </div>
                    {errorMessage && (
                        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                            {errorMessage}
                        </p>
                    )}
                    <Button
                        onClick={handleEnableMfa}
                        disabled={totpCode.length < 6 || loading}
                        loading={loading}
                        fullWidth
                    >
                        {t("center.signUp.mfaSubmit")}
                    </Button>
                    <button
                        type="button"
                        onClick={handleSkipMfa}
                        disabled={loading}
                        className="self-center text-sm text-content-tertiary transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        {t("auth.skipMfa")}
                    </button>
                </div>
            )}
        </div>
    );
}
